import type { DateRange } from 'react-day-picker'
import type { SRT_Column, SRT_Localization, SRT_RowData } from '../../types'

import * as React from 'react'
import { CalendarIcon, XCircle } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Separator } from '@/shared/components/ui/separator'

export function formatDate(
	date: Date | string | number | undefined,
	opts: Intl.DateTimeFormatOptions = {}
) {
	if (!date) return ''

	try {
		return new Intl.DateTimeFormat('en-US', {
			month: opts.month ?? 'long',
			day: opts.day ?? 'numeric',
			year: opts.year ?? 'numeric',
			...opts,
		}).format(new Date(date))
	} catch (_err) {
		return ''
	}
}

//import { formatDate } from '@/shared/lib/format'

type DateSelection = Date[] | DateRange

function getIsDateRange(value: DateSelection): value is DateRange {
	return value && typeof value === 'object' && !Array.isArray(value)
}

function parseAsDate(timestamp: number | string | undefined): Date | undefined {
	if (!timestamp) return undefined
	const numericTimestamp =
		typeof timestamp === 'string' ? Number(timestamp) : timestamp
	const date = new Date(numericTimestamp)

	return !Number.isNaN(date.getTime()) ? date : undefined
}

function parseColumnFilterValue(value: unknown) {
	if (value === null || value === undefined) {
		return []
	}

	if (Array.isArray(value)) {
		return value.map((item) => {
			if (typeof item === 'number' || typeof item === 'string') {
				return item
			}

			return undefined
		})
	}

	if (typeof value === 'string' || typeof value === 'number') {
		return [value]
	}

	return []
}

type SRT_TableDateFilterProps<TData extends SRT_RowData> = {
	column: SRT_Column<TData, unknown>
	title?: string
	multiple?: boolean
	localization: SRT_Localization
}

export function SRT_TableDateFilter<TData extends SRT_RowData>({
	column,
	title,
	multiple,
	localization,
}: SRT_TableDateFilterProps<TData>) {
	const columnFilterValue = column.getFilterValue()

	const selectedDates = React.useMemo<DateSelection>(() => {
		if (!columnFilterValue) {
			return multiple ? { from: undefined, to: undefined } : []
		}

		if (multiple) {
			const timestamps = parseColumnFilterValue(columnFilterValue)

			return {
				from: parseAsDate(timestamps[0]),
				to: parseAsDate(timestamps[1]),
			}
		}

		const timestamps = parseColumnFilterValue(columnFilterValue)
		const date = parseAsDate(timestamps[0])

		return date ? [date] : []
	}, [columnFilterValue, multiple])

	const onSelect = React.useCallback(
		(date: Date | DateRange | undefined) => {
			if (!date) {
				column.setFilterValue(undefined)

				return
			}

			if (multiple && !('getTime' in date)) {
				const from = date.from?.getTime()
				const to = date.to?.getTime()

				column.setFilterValue(from || to ? [from, to] : undefined)
			} else if (!multiple && 'getTime' in date) {
				column.setFilterValue(date.getTime())
			}
		},
		[column, multiple]
	)

	const onReset = React.useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation()
			column.setFilterValue(undefined)
		},
		[column]
	)

	const hasValue = React.useMemo(() => {
		if (multiple) {
			if (!getIsDateRange(selectedDates)) return false

			return selectedDates.from || selectedDates.to
		}
		if (!Array.isArray(selectedDates)) return false

		return selectedDates.length > 0
	}, [multiple, selectedDates])

	const formatDateRange = React.useCallback((range: DateRange) => {
		if (!range.from && !range.to) return ''
		if (range.from && range.to) {
			return `${formatDate(range.from)} - ${formatDate(range.to)}`
		}

		return formatDate(range.from ?? range.to)
	}, [])

	const label = React.useMemo(() => {
		if (multiple) {
			if (!getIsDateRange(selectedDates)) return null

			const hasSelectedDates = selectedDates.from || selectedDates.to
			const dateText = hasSelectedDates
				? formatDateRange(selectedDates)
				: 'Select date range'

			return (
				<span className='flex items-center gap-2'>
					<span>{title}</span>
					{hasSelectedDates && (
						<>
							<Separator
								orientation='vertical'
								className='mx-0.5 data-[orientation=vertical]:h-4'
							/>
							<span>{dateText}</span>
						</>
					)}
				</span>
			)
		}

		if (getIsDateRange(selectedDates)) return null

		const hasSelectedDate = selectedDates.length > 0
		const dateText = hasSelectedDate
			? formatDate(selectedDates[0])
			: 'Select date'

		return (
			<span className='flex items-center gap-2'>
				<span>{title}</span>
				{hasSelectedDate && (
					<>
						<Separator
							orientation='vertical'
							className='mx-0.5 data-[orientation=vertical]:h-4'
						/>
						<span>{dateText}</span>
					</>
				)}
			</span>
		)
	}, [selectedDates, multiple, formatDateRange, title])

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant='outline' className='border-dashed'>
					{hasValue ? (
						<div
							role='button'
							aria-label={`Clear ${title} filter`}
							tabIndex={0}
							onClick={onReset}
							className='rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-1 focus-visible:ring-ring/30 focus-visible:outline-none'
						>
							<XCircle />
						</div>
					) : (
						<CalendarIcon />
					)}
					{label}
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0' align='start'>
				{multiple ? (
					<Calendar
						captionLayout='dropdown'
						mode='range'
						locale={localization.getDateFnsLocalization?.()}
						selected={
							getIsDateRange(selectedDates)
								? selectedDates
								: { from: undefined, to: undefined }
						}
						onSelect={onSelect}
					/>
				) : (
					<Calendar
						captionLayout='dropdown'
						mode='single'
						locale={localization.getDateFnsLocalization?.()}
						selected={
							!getIsDateRange(selectedDates) ? selectedDates[0] : undefined
						}
						onSelect={onSelect}
					/>
				)}
			</PopoverContent>
		</Popover>
	)
}
